import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./TermsAndConditions.css";

const SECTIONS = [
  {
    id: "acceptance",
    num: "01",
    title: "Acceptance of Terms",
    content: `By registering, accessing, or using the Grow Biz International platform ("Platform"), you confirm that you have read, understood, and agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to any part of these Terms, you must not access or use the Platform.

These Terms apply to all users including members, investors, referrers, and visitors. Grow Biz International reserves the right to modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the revised Terms. You will be notified of material changes via email or a prominent notice on the Platform.`,
    points: [
      "You must be at least 18 years of age to register",
      "You must provide accurate and truthful registration information",
      "One account per individual — duplicate accounts will be terminated",
      "Corporate registrations require prior written approval",
    ],
  },
  {
    id: "membership",
    num: "02",
    title: "Membership & Activation",
    content: `Membership becomes active upon successful payment of your chosen investment package and verification of your identity documents. Grow Biz International operates a tiered membership structure — Starter, Silver, Gold, and Platinum — each carrying different investment levels and corresponding income benefits.`,
    points: [
      "Membership is non-transferable and personal to the registered individual",
      "Identity verification (CNIC copy) is required for all accounts",
      "Packages may be upgraded but not downgraded within an active cycle",
      "Activation is subject to sponsor ID validation and management approval",
    ],
  },
  {
    id: "income",
    num: "03",
    title: "Income Plan & Earnings",
    content: `The income plan includes Referral Bonuses, Level Income, ROI Income, Profit Sharing, and Rank Rewards. All income projections are indicative and not guaranteed. Actual earnings depend on individual performance, team activity, and the company's overall business results.`,
    points: [
      "Referral bonuses are paid upon activation of referred members only",
      "Level income is calculated weekly based on active downline volumes",
      "ROI income is subject to the performance of underlying real estate assets",
      "Profit sharing distributions require a minimum active investment balance",
      "Rank rewards are one-time payments upon achieving and maintaining rank criteria",
    ],
  },
  {
    id: "withdrawals",
    num: "04",
    title: "Withdrawals & Payments",
    content: `Members may withdraw earned balances subject to meeting minimum withdrawal thresholds and completion of identity verification. Grow Biz International processes withdrawals within 24–72 business hours via bank transfer, JazzCash, or EasyPaisa.`,
    points: [
      "Minimum withdrawal amount is PKR 1,000 per transaction",
      "Withdrawal fees may apply and will be disclosed at time of request",
      "The company reserves the right to delay withdrawals for compliance checks",
      "Funds subject to active disputes or investigations cannot be withdrawn",
      "Withdrawal methods may be restricted based on country of residence",
    ],
  },
  {
    id: "prohibited",
    num: "05",
    title: "Prohibited Activities",
    content: `To maintain the integrity and legality of the Platform, the following activities are strictly prohibited. Violation of any prohibition may result in immediate account suspension, forfeiture of earnings, and potential legal action.`,
    points: [
      "Creating or operating multiple accounts under any circumstances",
      "Providing false, misleading, or fabricated information during registration",
      "Misrepresenting the income plan or making guaranteed income claims",
      "Engaging in any form of money laundering or financing of illegal activities",
      "Hacking, scraping, or reverse-engineering any part of the Platform",
      "Recruiting members through spam, harassment, or deceptive marketing",
    ],
  },
  {
    id: "intellectual",
    num: "06",
    title: "Intellectual Property",
    content: `All content on the Platform — including logos, branding, text, graphics, software, and materials — is the exclusive intellectual property of Grow Biz International or its licensors. No part of the Platform may be reproduced, distributed, or used commercially without prior written permission.`,
    points: [
      "Members may share referral links and approved marketing materials only",
      "Third-party use of the Grow Biz brand requires written authorization",
      "User-generated content grants Grow Biz a non-exclusive usage license",
    ],
  },
  {
    id: "privacy",
    num: "07",
    title: "Privacy & Data",
    content: `Grow Biz International collects and processes personal data in accordance with applicable data protection laws. By using the Platform, you consent to the collection, processing, and use of your personal information as described in our Privacy Policy. We do not sell personal data to third parties.`,
    points: [
      "Personal data is used solely for account management, payments, and compliance",
      "You may request access to, correction of, or deletion of your personal data",
      "Data may be shared with regulatory authorities when legally required",
      "We use industry-standard encryption to protect your data in transit and at rest",
    ],
  },
  {
    id: "termination",
    num: "08",
    title: "Termination & Suspension",
    content: `Grow Biz International reserves the right to suspend or permanently terminate any account at its sole discretion, with or without prior notice, for violation of these Terms, suspicious activity, regulatory requirements, or any conduct deemed harmful to the Platform or its members.`,
    points: [
      "Account termination results in forfeiture of unearned bonuses and pending referrals",
      "Earned and withdrawable balances will be paid out after a 30-day investigation period",
      "Members may appeal termination decisions in writing within 14 days",
      "Grow Biz reserves the right to refuse re-registration after termination",
    ],
  },
  {
    id: "liability",
    num: "09",
    title: "Limitation of Liability",
    content: `To the maximum extent permitted by applicable law, Grow Biz International shall not be liable for indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Platform or investment activities, even if we have been advised of the possibility of such damages.`,
    points: [
      "Real estate investment carries inherent market and liquidity risks",
      "Past performance of the income plan does not guarantee future results",
      "We are not liable for losses arising from system downtime or technical failures",
      "Maximum liability shall not exceed the total amount invested by the member",
    ],
  },
  {
    id: "governing",
    num: "10",
    title: "Governing Law & Disputes",
    content: `These Terms are governed by and construed in accordance with the laws of the Islamic Republic of Pakistan. Any disputes arising from or in connection with these Terms shall first be attempted to be resolved through good-faith negotiation, and failing that, through arbitration in Karachi, Pakistan.`,
    points: [
      "You agree to submit to the exclusive jurisdiction of Pakistani courts",
      "Arbitration proceedings shall be conducted in the English or Urdu language",
      "Each party bears its own costs unless otherwise awarded by the arbitrator",
    ],
  },
];

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(id);
        },
        { rootMargin: "-30% 0px -60% 0px" },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, [ids]);
  return active;
}

const SECTION_IDS = SECTIONS.map((s) => s.id);

export default function TermsAndConditions() {
  const progress = useScrollProgress();
  const activeId = useActiveSection(SECTION_IDS);
  const [tocOpen, setTocOpen] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTocOpen(false);
    }
  };

  return (
    <div className="tc-page">
      {/* progress bar */}
      <div className="tc-progress" style={{ width: `${progress}%` }} />

      {/* bokeh */}
      <div className="tc-bokeh tc-bokeh--1" aria-hidden="true" />
      <div className="tc-bokeh tc-bokeh--2" aria-hidden="true" />
      <div className="tc-bokeh tc-bokeh--3" aria-hidden="true" />

      {/* ── Hero banner ── */}
      <div className="tc-hero">
        <div className="tc-hero-glow" aria-hidden="true" />
        <div className="tc-hero-inner">
          <div className="tc-hero-logo">
            <div className="tc-logo-circle">G</div>
            <span className="tc-logo-text">
              Grow Biz <em>International</em>
            </span>
          </div>
          <span className="tc-label">Legal Documentation</span>
          <h1 className="tc-title">
            Terms &amp; <span className="tc-gold">Conditions</span>
          </h1>
          <div className="tc-gline" />
          <p className="tc-sub">
            Please read these terms carefully before using the Grow Biz
            International platform. By registering or investing, you agree to be
            bound by the following terms.
          </p>
          <div className="tc-meta">
            <div className="tc-meta-item">
              <span className="tc-meta-label">Effective Date</span>
              <span className="tc-meta-value">1 January 2026</span>
            </div>
            <div className="tc-meta-divider" />
            <div className="tc-meta-item">
              <span className="tc-meta-label">Last Updated</span>
              <span className="tc-meta-value">1 March 2026</span>
            </div>
            <div className="tc-meta-divider" />
            <div className="tc-meta-item">
              <span className="tc-meta-label">Jurisdiction</span>
              <span className="tc-meta-value">Pakistan</span>
            </div>
            <div className="tc-meta-divider" />
            <div className="tc-meta-item">
              <span className="tc-meta-label">Sections</span>
              <span className="tc-meta-value">{SECTIONS.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="tc-body">
        {/* Sticky TOC sidebar */}
        <aside className={`tc-toc${tocOpen ? " tc-toc--open" : ""}`}>
          <div className="tc-toc-header">
            <span>Contents</span>
            <button
              className="tc-toc-close"
              onClick={() => setTocOpen(false)}
              aria-label="Close table of contents"
            >
              ✕
            </button>
          </div>
          <nav>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                className={`tc-toc-item${activeId === s.id ? " tc-toc-item--active" : ""}`}
                onClick={() => scrollTo(s.id)}
              >
                <span className="tc-toc-num">{s.num}</span>
                <span className="tc-toc-label">{s.title}</span>
              </button>
            ))}
          </nav>
          <div className="tc-toc-footer">
            <Link to="/join-us" className="tc-toc-cta">
              Accept &amp; Register →
            </Link>
          </div>
        </aside>

        {/* Mobile TOC toggle */}
        <button
          className="tc-toc-toggle"
          onClick={() => setTocOpen(true)}
          aria-label="Open table of contents"
        >
          <span />
          <span />
          <span />
          <span className="tc-toc-toggle-label">Contents</span>
        </button>

        {/* Overlay */}
        {tocOpen && (
          <div
            className="tc-overlay"
            onClick={() => setTocOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main content */}
        <main className="tc-main">
          {/* Notice banner */}
          <div className="tc-notice">
            <div className="tc-notice-icon">⚠</div>
            <p>
              These Terms constitute a legally binding agreement. By using the
              Platform you confirm that you are at least 18 years old and have
              the legal capacity to enter into this agreement under Pakistani
              law.
            </p>
          </div>

          {/* Sections */}
          {SECTIONS.map((sec, idx) => (
            <section
              key={sec.id}
              id={sec.id}
              className="tc-section"
              style={{ "--idx": idx }}
            >
              <div className="tc-sec-head">
                <div className="tc-sec-num">{sec.num}</div>
                <h2 className="tc-sec-title">{sec.title}</h2>
              </div>
              <div className="tc-sec-divider" />
              <p className="tc-sec-body">{sec.content}</p>
              {sec.points?.length > 0 && (
                <ul className="tc-points">
                  {sec.points.map((pt, i) => (
                    <li key={i} className="tc-point">
                      <span className="tc-point-dot" aria-hidden="true" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Contact box */}
          <div className="tc-contact-box">
            <div className="tc-contact-box-top" />
            <h3>Questions About These Terms?</h3>
            <p>
              If you have any questions, concerns, or require clarification
              regarding these Terms and Conditions, please do not hesitate to
              contact us.
            </p>
            <div className="tc-contact-grid">
              {[
                { icon: "📧", label: "Email", val: "legal@growbizintl.com" },
                { icon: "💬", label: "WhatsApp", val: "+92-300-GROWBIZ" },
                {
                  icon: "📍",
                  label: "Address",
                  val: "Clifton Block 9, Karachi, Pakistan",
                },
              ].map((c) => (
                <div key={c.label} className="tc-contact-item">
                  <div className="tc-contact-icon">{c.icon}</div>
                  <div>
                    <div className="tc-contact-label">{c.label}</div>
                    <div className="tc-contact-val">{c.val}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accept CTA */}
          <div className="tc-accept">
            <p>
              By clicking "Accept &amp; Register" you confirm that you have read
              and agree to all of the above Terms and Conditions.
            </p>
            <div className="tc-accept-btns">
              <Link to="/join-us" className="tc-btn-accept">
                Accept &amp; Register
              </Link>
              <Link to="/" className="tc-btn-decline">
                Back to Home
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
